// THE POOL WORKER (R8c1, client/src/pool.js): the worker entry of the ONE
// client worker family, and the op registry both sides run. Bundled on its
// own and inlined as __POOL_WORKER_SRC__ (scripts/build-client.js); pool.js
// imports OPS / handle from here and, without a Worker (node, the dashboard
// bundle), runs the same handle() in-realm — one code path.
//
// Messages (main thread -> worker):
//   { id, op, args, wasm? }   wasm: { name, module? } — the artefact the op
//                             runs on; `module` (a WebAssembly.Module, sent
//                             with the first job of this worker that names
//                             it) is instantiated here once and cached by
//                             name, later jobs send the name alone
// Answers (worker -> main thread):
//   { id, result }            the op's result, its `transfer` list moved back
//   { id, error }             the op threw (a message string); the worker lives on
// An uncaught failure (the worker itself gone) is the pool's onerror.
//
// An op: (args, ctx) -> { result, transfer? } | Promise of it; ctx.wasm = the
// artefact's instance exports (null when the job names none). Ops are PURE:
// no DOM, no sched.js, no state outside what the job carries (a stateful op,
// R8c2+, keeps it per worker under the job's affinity key).
// The ops live in their own pure modules (pool-<family>.js), one import each;
// the worker bundle is this file + those (no three core, no DOM).
import { forestShell, glbSoup, canopyGrey } from './pool-forest.js';   // R8c4: forest.shell, glb.soup; canopy.grey
import { VEG_OPS } from './pool-veg.js';   // R8c3: veg.paint / veg.sprite / veg.page (affinity 'veg'), trees.tile
import { STRUCT_OPS } from './pool-structures.js';   // R8c2: structures (decode + collider index), structures.draw (the draw's arrays)
import { GENOME_OPS } from './pool-genome.js';   // R8c5: genome.params (the ring's genome record solve)
import { RING_OPS } from './pool-ring.js';   // far-field-load: ring.quick (a far sector's first colour)
import { GROUND_OPS } from './pool-ground.js';   // quest-move: ground.index (a GM03 L0 node's GMIX1 index)

// the transferable ArrayBuffers under v (typed arrays' buffers; plain objects / arrays walked)
function buffersOf(v, out = new Set()) {
  if (v instanceof ArrayBuffer) out.add(v);
  else if (ArrayBuffer.isView(v)) { if (v.buffer instanceof ArrayBuffer) out.add(v.buffer); }
  else if (Array.isArray(v)) for (const x of v) buffersOf(x, out);
  else if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) for (const k in v) buffersOf(v[k], out);
  return out;
}

export const OPS = {
  // TEST OP (R8c1, no consumer): the args back, every buffer in them
  // transferred back (the in / out transfer path)
  echo: (args) => ({ result: args, transfer: [...buffersOf(args)] }),
  // TEST OP (R8c1, no consumer): the posted-Module path — args.xs
  // (Float64Array) through vc_alloc'ed linear memory and back (vc_alloc /
  // vc_free, the raw C ABI every vrm_* artefact has), summed; + the
  // artefact's kernel count (vc_kernel_count, its registry)
  'wasm.sum': (args, ctx) => {
    const e = ctx.wasm;
    if (!e) throw new Error('wasm.sum: no wasm artefact on the job');
    const xs = args.xs, n = xs.length, nb = 8 * n;
    const p = e.vc_alloc(nb);
    new Uint8Array(e.memory.buffer, p, nb).set(new Uint8Array(xs.buffer, xs.byteOffset, nb));
    const dv = new DataView(e.memory.buffer, p, nb);   // a fresh view: vc_alloc may have grown the memory
    let sum = 0;
    for (let i = 0; i < n; i++) sum += dv.getFloat64(8 * i, true);
    e.vc_free(p, nb);
    return { result: { sum, n, kernels: typeof e.vc_kernel_count === 'function' ? e.vc_kernel_count() : null } };
  },
  // R8c4 (pool-forest.js): the forest shell decode / group build, a GLBQ
  // tile's soup in cell order
  'forest.shell': forestShell,
  'glb.soup': glbSoup,
  // gs-canopygen-slice (pool-forest.js): an SHD2 grey tile's R channel (canopygen.js)
  'canopy.grey': canopyGrey,
  ...VEG_OPS,   // R8c3 (pool-veg.js): the vegetation atlases, the tree tiles' index
  ...STRUCT_OPS,   // R8c2 (pool-structures.js): structures.bin decode + collider index, the structure draw's arrays
  ...GENOME_OPS,   // R8c5 (pool-genome.js): genome.params, one genome record's genomeTexParams
  ...RING_OPS,   // far-field-load (pool-ring.js): ring.quick, a far sector's quick paint (ringref ringPaint)
  ...GROUND_OPS,   // quest-move (pool-ground.js): ground.index, a GM03 L0 node decoded + indexed for ClientGround
};

// one job: m = { id, op, args, wasm? } -> { msg: {id, result} | {id, error}, transfer };
// cache: Map name -> Promise of the artefact's instance exports (this realm's)
export async function handle(m, cache) {
  try {
    const fn = Object.prototype.hasOwnProperty.call(OPS, m.op) ? OPS[m.op] : null;
    if (!fn) throw new Error(`no op ${m.op}`);
    let wasm = null;
    if (m.wasm) {
      let p = cache.get(m.wasm.name);
      if (!p) {
        if (!(m.wasm.module instanceof WebAssembly.Module)) throw new Error(`no module for wasm ${m.wasm.name}`);
        p = WebAssembly.instantiate(m.wasm.module, {}).then((inst) => inst.exports);
        cache.set(m.wasm.name, p);
      }
      wasm = await p;
    }
    const out = (await fn(m.args, { wasm })) || {};
    return { msg: { id: m.id, result: out.result }, transfer: out.transfer || [] };
  } catch (e) {
    return { msg: { id: m.id, error: (e && e.message) || String(e) }, transfer: [] };
  }
}

// the worker entry: only inside a worker (a page / node importing OPS starts nothing)
/* global WorkerGlobalScope */
if (typeof WorkerGlobalScope !== 'undefined' && typeof self !== 'undefined' && self instanceof WorkerGlobalScope) {
  const cache = new Map();
  self.onmessage = (e) => {
    handle(e.data, cache).then(({ msg, transfer }) => self.postMessage(msg, transfer));
  };
}
